/**
 * A real .xlsx built in the browser — for the list pages that export what they
 * show (Stock levels, Items).
 *
 * Styled, but kept to what every spreadsheet app opens. The owner's phone once
 * choked on a workbook with notes, hidden columns and a second sheet, so it gets
 * ONE sheet, no comments, no hidden or merged cells, no formulas (a viewer that
 * doesn't calculate would show 0): a title, a line or two saying what this is,
 * a dark header row that stays put while scrolling and carries the filter
 * arrows, light banding, and bold totals under the data.
 *
 * Numbers go in as numbers so the sheet can sum them. Money keeps its own
 * currency's decimals (IQD none, USD two, up to four where a cost has them) and
 * is never added across currencies: a total per currency is a row of its own.
 *
 * exceljs is its own prebuilt browser file, fetched on the first export only and
 * never run through the app's bundler — see exceljsAsset() in vite.config.ts.
 */
import type * as ExcelJS from 'exceljs';

import { downloadBlob } from './csv.js';
import { getCurrencyDecimals } from './money.js';

export type XlsxValue =
  | string
  | number
  | null
  | undefined
  | { money: number; currency: string | null | undefined };

export interface XlsxColumn {
  header: string;
  /**
   * text: as is. code: a code or barcode — kept exact, as a number only when it
   * is plain digits Excel can hold without losing any (so no green "number
   * stored as text" flag on every row, and no lost leading 0). number: a
   * quantity. money: `{ money, currency }`, formatted in that currency.
   */
  kind?: 'text' | 'code' | 'number' | 'money';
  /**
   * money: a unit cost or price, shown with up to 4 decimals where it has them
   * (a bill line of 0.2121). Amounts and totals show the currency's own.
   */
  unitPrice?: boolean;
  /**
   * money: the currency the header names. A cell in another currency then
   * carries its own code ("2.75 USD"), so one odd price can't pass for the
   * column's.
   */
  currency?: string | null;
}

export interface XlsxExport {
  fileName: string;
  sheetName: string;
  title: string;
  /** Short lines under the title: when, how many, which filters. */
  info?: string[];
  rtl?: boolean;
  columns: XlsxColumn[];
  rows: XlsxValue[][];
  /** Bold rows under the data, outside the filter range. */
  totals?: XlsxValue[][];
  /**
   * Which data rows are shaded (by index). Default: every other row. A sheet
   * with several rows per product shades by product instead, so a product's
   * rows read as one block.
   */
  band?: (rowIndex: number) => boolean;
}

const INK = 'FF16233B';
const MUTED = 'FF64748B';
const BAND = 'FFF4F6FA';
const TOTAL_FILL = 'FFE6EBF3';
const LINE = 'FFCBD3E1';

/**
 * The invisible direction marks the app's date/number formatting wraps RTL text
 * in (lib/intl isolateRtl). A browser hides them; some phone viewers draw a box.
 */
const ISOLATES = /[\u2066-\u2069]/g;
const clean = (s: string): string => s.replace(ISOLATES, '');

/** Plain digits Excel keeps exactly: no leading 0, at most 15 of them. */
const SAFE_DIGITS = /^(0|[1-9]\d{0,14})$/;

export function moneyFormat(currency: string | null | undefined, unitPrice = false): string {
  const d = getCurrencyDecimals(currency) ?? 2;
  if (d <= 0) return '#,##0';
  return `#,##0.${'0'.repeat(d)}${unitPrice ? '#'.repeat(Math.max(0, 4 - d)) : ''}`;
}

function quantityFormat(n: number): string {
  return Number.isInteger(n) ? '#,##0' : '#,##0.####';
}

/** Excel refuses these in a sheet name, and caps it at 31 characters. */
export function safeSheetName(name: string): string {
  const s = name.replace(/[[\]:*?/\\]/g, ' ').replace(/^'+|'+$/g, '').trim().slice(0, 31).trim();
  return s || 'Sheet1';
}

/** A rough display width, in characters, of what a cell will show. */
function shownLength(v: XlsxValue): number {
  if (v === null || v === undefined) return 0;
  if (typeof v === 'string') return v.length;
  const n = typeof v === 'number' ? v : v.money;
  return Math.abs(n).toLocaleString('en', { maximumFractionDigits: 4 }).length + 1;
}

type ExcelModule = typeof ExcelJS;
let loading: Promise<ExcelModule> | null = null;

/** Where the build put that file (vite.config.ts defines it). */
declare const __EXCELJS_URL__: string;

/** The UMD file sets window.ExcelJS; one load per page, retried after a failure. */
function loadExcel(): Promise<ExcelModule> {
  const w = window as unknown as { ExcelJS?: ExcelModule };
  if (w.ExcelJS) return Promise.resolve(w.ExcelJS);
  loading ??= new Promise<ExcelModule>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = __EXCELJS_URL__;
    s.async = true;
    s.onload = () => (w.ExcelJS ? resolve(w.ExcelJS) : reject(new Error('exceljs did not load')));
    s.onerror = () => reject(new Error('exceljs did not load'));
    document.head.appendChild(s);
  }).catch((e: unknown) => {
    loading = null;
    throw e;
  });
  return loading;
}

/** `excel` is for tests, which hand in the node build. */
export async function buildXlsx(x: XlsxExport, excel?: ExcelModule): Promise<ArrayBuffer> {
  const Excel = excel ?? (await loadExcel());

  const n = x.columns.length;
  const info = (x.info ?? []).filter(Boolean);
  // Title, its info lines, one blank row, then the header.
  const headerRow = 1 + info.length + 2;

  const wb = new Excel.Workbook();
  wb.created = new Date();
  const ws = wb.addWorksheet(safeSheetName(x.sheetName), {
    views: [{ state: 'frozen', xSplit: 0, ySplit: headerRow, rightToLeft: !!x.rtl }],
    pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  const title = ws.getCell(1, 1);
  title.value = clean(x.title);
  title.font = { bold: true, size: 15, color: { argb: INK } };
  ws.getRow(1).height = 24;
  info.forEach((line, i) => {
    const c = ws.getCell(2 + i, 1);
    c.value = clean(line);
    c.font = { size: 10, color: { argb: MUTED } };
  });

  const head = ws.getRow(headerRow);
  head.height = 22;
  x.columns.forEach((col, i) => {
    const c = head.getCell(i + 1);
    c.value = clean(col.header);
    c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: INK } };
    c.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  });

  const put = (rowNo: number, values: XlsxValue[], style: { band?: boolean; total?: boolean }): void => {
    const row = ws.getRow(rowNo);
    for (let i = 0; i < n; i++) {
      const col = x.columns[i]!;
      const v = values[i];
      const c = row.getCell(i + 1);
      if (v !== null && v !== undefined) {
        if (typeof v === 'object') {
          c.value = Number.isFinite(v.money) ? v.money : null;
          c.numFmt = moneyFormat(v.currency, col.unitPrice);
          if (col.currency && v.currency && v.currency !== col.currency) c.numFmt += ` "${v.currency}"`;
        } else if (typeof v === 'number') {
          c.value = Number.isFinite(v) ? v : null;
          c.numFmt = col.kind === 'money' ? '#,##0.####' : quantityFormat(v);
        } else if (col.kind === 'code' && SAFE_DIGITS.test(v.trim())) {
          c.value = Number(v.trim());
          c.numFmt = '0';
        } else {
          c.value = clean(v);
          // On a right-to-left sheet a Latin name would sit at the far edge of
          // its cell, away from the Arabic ones; this lines them all up.
          if (x.rtl && (col.kind !== 'code' || style.total)) c.alignment = { horizontal: 'right' };
        }
        // A code is a number OR text (a leading 0) and the two align apart; centred, they don't.
        // (A totals label in that column is not a code: it reads from the edge.)
        if (col.kind === 'code' && !style.total) c.alignment = { horizontal: 'center' };
      }
      if (style.total) {
        c.font = { bold: true, color: { argb: INK } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTAL_FILL } };
        c.border = { top: { style: 'thin', color: { argb: LINE } } };
      } else if (style.band) {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BAND } };
      }
    }
  };

  x.rows.forEach((r, i) => put(headerRow + 1 + i, r, { band: x.band ? x.band(i) : i % 2 === 1 }));
  const lastData = headerRow + Math.max(x.rows.length, 1);
  // One empty row first: Excel grows a filter over every row touching the data,
  // and a totals row inside it would be sorted in with the products.
  (x.totals ?? []).forEach((r, i) => put(lastData + 2 + i, r, { total: true }));

  ws.autoFilter = { from: { row: headerRow, column: 1 }, to: { row: lastData, column: n } };

  // Width from the header and a sample of the rows; long names wrap at 48.
  const sample = x.rows.length > 2000 ? x.rows.slice(0, 2000) : x.rows;
  x.columns.forEach((col, i) => {
    let w = col.header.length + 5; // room for the filter arrow
    for (const r of sample) w = Math.max(w, shownLength(r[i]) + 2);
    // A totals label spills into the empty cell beside it; only its numbers size the column.
    for (const r of x.totals ?? []) if (typeof r[i] !== 'string') w = Math.max(w, shownLength(r[i]) + 2);
    ws.getColumn(i + 1).width = Math.min(48, Math.max(8, w));
  });

  return (await wb.xlsx.writeBuffer()) as ArrayBuffer;
}

export async function downloadXlsx(x: XlsxExport): Promise<void> {
  const buf = await buildXlsx(x);
  downloadBlob(
    x.fileName,
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
  );
}
